import { useEffect, useMemo, useState } from "react";

import { FieldArray, useFormikContext } from "formik";

import { Button, ButtonsWrapper } from "components/elements";
import {
  SelectField,
  TextField,
  TiVoTargetsField,
} from "components/form/fields";
import { TARGET_FIELDS } from "components/form/fields/TiVoTargets";
import {
  Column,
  Row,
  Section,
  SubTab,
  SubTabs,
  TabContent,
} from "components/layout";
import { ReleaseStage } from "hooks/tivoApps";
import { getFieldTestId } from "utils/formik";
import { isTabInvalid } from "utils/validation";

import { PresentationConfigs } from ".";
import { TEXTS } from "../constants";
import { FIELDS_PRESENTATION, PROVIDER_FIELDS } from "../fields";
import { ProviderForm } from "../types";
import { getInitialPresentationConfig } from "../utils";
import styles from "./PresentationConfigsByStage.scss";

function getTabFields(name: string) {
  return Object.values(FIELDS_PRESENTATION).map(
    (field) => `${name}.${field.name}`
  );
}

function createEmptyStagePresentation(selectedStage: ReleaseStage) {
  return {
    releaseStage: selectedStage,
    configs: [getInitialPresentationConfig(true)],
    label: "",
    targets: {},
    partnerPicks: {
      aspectRatio: "",
      reporting: "",
      type: "",
    },
  };
}

export function PresentationConfigsByStage({
  selectedStage,
}: {
  selectedStage: ReleaseStage;
}) {
  const [activePresentationIndex, setActivePresentationIndex] = useState(0);
  const {
    errors,
    values: { presentations },
  } = useFormikContext<ProviderForm>();

  const stagePresentations = useMemo(
    () =>
      presentations
        .map((presentation, globalPresentationIndex) => ({
          presentation,
          globalPresentationIndex,
        }))
        .filter(
          ({ presentation }) => presentation.releaseStage === selectedStage
        ),
    [presentations, selectedStage]
  );
  const testId = getFieldTestId(
    `${PROVIDER_FIELDS.PRESENTATION.RELEASE_STAGE_PRESENTATIONS.name}.${selectedStage}`
  );

  useEffect(() => {
    setActivePresentationIndex(0);
  }, [selectedStage]);

  const activePresentation =
    stagePresentations[activePresentationIndex] ?? stagePresentations[0];

  return (
    <FieldArray
      name={PROVIDER_FIELDS.PRESENTATION.RELEASE_STAGE_PRESENTATIONS.name}
    >
      {({ push, remove, name }) => {
        const selectedStagePath = `${name}.${activePresentation?.globalPresentationIndex ?? 0}`;

        return (
          <>
            <SubTabs>
              {stagePresentations.map(
                ({ presentation, globalPresentationIndex }, index) => (
                  <SubTab
                    // eslint-disable-next-line react/no-array-index-key
                    key={globalPresentationIndex}
                    active={index === activePresentationIndex}
                    text={
                      presentation.label || TEXTS.PRESENTATION.NEW_CONFIG_LABEL
                    }
                    icon={
                      !isTabInvalid(
                        getTabFields(`${name}.${globalPresentationIndex}`),
                        errors
                      )
                        ? "ok"
                        : "warning"
                    }
                    iconColor={
                      !isTabInvalid(
                        getTabFields(`${name}.${globalPresentationIndex}`),
                        errors
                      )
                        ? "success"
                        : "error"
                    }
                    onClick={() => setActivePresentationIndex(index)}
                  />
                )
              )}
              <SubTab
                active={false}
                className={styles.addButton}
                icon="plus"
                look="blue"
                text={TEXTS.PRESENTATION.ADD_CONFIG_BUTTON_LABEL}
                dataTestId={`${testId}-add-button`}
                onClick={() => {
                  push(createEmptyStagePresentation(selectedStage));
                }}
              />
            </SubTabs>
            <TabContent>
              {activePresentation ? (
                <>
                  <ButtonsWrapper>
                    <Button
                      dataTestId={`${testId}-delete-button`}
                      onClick={() => {
                        setActivePresentationIndex((index) =>
                          Math.max(index - 1, 0)
                        );
                        remove(activePresentation.globalPresentationIndex);
                      }}
                    >
                      {TEXTS.PRESENTATION.DELETE_CONFIG_BUTTON_LABEL}
                    </Button>
                  </ButtonsWrapper>
                  <Section>
                    <Row>
                      <Column>
                        <TextField
                          {...FIELDS_PRESENTATION.LABEL}
                          name={`${selectedStagePath}.${FIELDS_PRESENTATION.LABEL.name}`}
                        />
                      </Column>
                      <Column>
                        <div className={styles.targetsField}>
                          <TiVoTargetsField
                            {...FIELDS_PRESENTATION.TARGETS}
                            fields={[TARGET_FIELDS.oems]}
                            name={`${selectedStagePath}.${FIELDS_PRESENTATION.TARGETS.name}`}
                            enableSlugBulkSelection={true}
                          />
                        </div>
                      </Column>
                    </Row>
                  </Section>
                  <Section>
                    <PresentationConfigs
                      name={`${selectedStagePath}.${FIELDS_PRESENTATION.CONFIGS.name}`}
                    />
                  </Section>
                  <Section header={TEXTS.PRESENTATION.PARTNER_PICKS_HEADER}>
                    <Row>
                      <Column>
                        <TextField
                          {...FIELDS_PRESENTATION.PARTNER_PICKS_ASPECT_RATIO}
                          name={`${selectedStagePath}.${FIELDS_PRESENTATION.PARTNER_PICKS_ASPECT_RATIO.name}`}
                        />
                        <SelectField
                          {...FIELDS_PRESENTATION.PARTNER_PICKS_TYPE}
                          name={`${selectedStagePath}.${FIELDS_PRESENTATION.PARTNER_PICKS_TYPE.name}`}
                        />
                      </Column>
                      <Column>
                        <SelectField
                          {...FIELDS_PRESENTATION.PARTNER_PICKS_REPORTING}
                          name={`${selectedStagePath}.${FIELDS_PRESENTATION.PARTNER_PICKS_REPORTING.name}`}
                        />
                      </Column>
                    </Row>
                  </Section>
                </>
              ) : null}
            </TabContent>
          </>
        );
      }}
    </FieldArray>
  );
}
